import { BookOpen } from 'lucide-react';
import type { ReactNode } from 'react';
import type { HelpKey } from '@/help';
import { cn } from '@/lib/cn';
import { useAppStore } from '@/lib/store';
import { Button } from './button';

export interface EmptyStateProps {
  /** Illustration or large icon. */
  readonly illustration?: ReactNode;
  readonly title: ReactNode;
  readonly body?: ReactNode;
  /** Primary next step (a `<Button>` or `<Button asChild><Link/></Button>`). */
  readonly action?: ReactNode;
  /** Secondary action. */
  readonly secondary?: ReactNode;
  /** Shows "Learn more" that opens this help article in the help panel. */
  readonly helpKey?: HelpKey;
  readonly className?: string;
  readonly compact?: boolean;
}

/** Empty screens teach what to do next: say what goes here and offer the action that fills it. */
export const EmptyState = ({ illustration, title, body, action, secondary, helpKey, className, compact }: EmptyStateProps) => {
  const openHelp = useAppStore((s) => s.openHelp);
  return (
    <div className={cn('flex flex-col items-center text-center', compact ? 'gap-2 py-6' : 'gap-3 px-4 py-12', className)}>
      {illustration ? (
        <div className="mb-1 flex size-14 items-center justify-center rounded-full bg-primary-soft text-primary [&_svg]:size-6" aria-hidden>
          {illustration}
        </div>
      ) : null}
      <h2 className={cn('font-semibold text-text', compact ? 'text-sm' : 'text-lg')}>{title}</h2>
      {body ? <p className="max-w-md text-sm text-text-muted">{body}</p> : null}
      {action || secondary || helpKey ? (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          {action}
          {secondary}
          {helpKey ? (
            <Button variant="ghost" size="sm" icon={<BookOpen aria-hidden />} onClick={() => openHelp(helpKey)}>
              Learn more
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
